import { useMemo, useRef, useState } from 'react';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import MainCard from 'components/MainCard';
import IconButton from 'components/@extended/IconButton';
import Transitions from 'components/@extended/Transitions';
import { useWs } from 'contexts/WsContext';

// assets
import BellOutlined from '@ant-design/icons/BellOutlined';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import MessageOutlined from '@ant-design/icons/MessageOutlined';

const avatarSX = { width: 36, height: 36, fontSize: '1rem' };
const actionSX = { mt: '6px', ml: 1, top: 'auto', right: 'auto', alignSelf: 'flex-start', transform: 'none' };

function fmtTime(ts) {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

export default function Notification() {
  const downMD = useMediaQuery((theme) => theme.breakpoints.down('md'));

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  const { isConnected, unreadByKey, latestByKey, clearAllUnread, markRead } = useWs();

  // total unread (sum semua chat)
  const unreadCount = useMemo(() => {
    let sum = 0;
    for (const v of Object.values(unreadByKey || {})) sum += Number(v || 0);
    return sum;
  }, [unreadByKey]);

  // list notif: hanya chat yang unread>0, ambil preview dari latestByKey
  const items = useMemo(() => {
    const rows = [];

    for (const [key, unread] of Object.entries(unreadByKey || {})) {
      const n = Number(unread || 0);
      if (n <= 0) continue;

      const latest = latestByKey?.[key];
      const sessionName = latest?.sessionName || key.split('|')[0] || '-';
      const chatId = latest?.chatId || key.split('|')[1] || '-';
      const preview = (latest?.preview || '').trim();
      const ts = latest?.ts || Date.now();

      rows.push({
        id: key,
        key,
        sessionName,
        chatId,
        unread: n,
        preview: preview || '(no preview)',
        ts
      });
    }

    // sort terbaru di atas
    rows.sort((a, b) => Number(b.ts || 0) - Number(a.ts || 0));
    return rows.slice(0, 20);
  }, [unreadByKey, latestByKey]);

  const handleToggle = () => setOpen((v) => !v);

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) return;
    setOpen(false);
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <IconButton
        color="secondary"
        variant="light"
        sx={() => ({ color: 'text.primary', bgcolor: open ? 'grey.100' : 'transparent' })}
        aria-label="open notification"
        ref={anchorRef}
        aria-controls={open ? 'notification-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Badge badgeContent={unreadCount} color="primary">
          <BellOutlined />
        </Badge>
      </IconButton>

      <Popper
        placement={downMD ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [downMD ? -5 : 0, 9] } }] }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position={downMD ? 'top' : 'top-right'} in={open} {...TransitionProps}>
            <Paper sx={(theme) => ({ boxShadow: theme.customShadows.z1, width: '100%', minWidth: 285, maxWidth: { xs: 285, md: 420 } })}>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h6">Notification</Typography>
                      <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        {isConnected ? 'WS: connected' : 'WS: disconnected'}
                      </Typography>
                    </Box>
                  }
                  elevation={0}
                  border={false}
                  content={false}
                  secondary={
                    <>
                      {items.length > 0 && (
                        <Tooltip title="Mark all as read">
                          <IconButton color="success" size="small" onClick={clearAllUnread}>
                            <CheckCircleOutlined style={{ fontSize: '1.15rem' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  }
                >
                  <List
                    component="nav"
                    sx={{
                      p: 0,
                      '& .MuiListItemButton-root': {
                        py: 0.5,
                        px: 2,
                        '&.Mui-selected': { bgcolor: 'grey.50', color: 'text.primary' },
                        '& .MuiAvatar-root': avatarSX,
                        '& .MuiListItemSecondaryAction-root': { ...actionSX, position: 'relative' }
                      }
                    }}
                  >
                    {items.length === 0 ? (
                      <Box sx={{ p: 2 }}>
                        <Typography variant="body2" sx={{ opacity: 0.7 }}>
                          No notifications yet.
                        </Typography>
                      </Box>
                    ) : (
                      items.map((n) => (
                        <ListItem
                          key={n.id}
                          component={ListItemButton}
                          divider
                          secondaryAction={
                            <Typography variant="caption" noWrap>
                              {fmtTime(n.ts)}
                            </Typography>
                          }
                          onClick={() => {
                            // notifnya ikut “read” juga
                            markRead(n.sessionName, n.chatId);
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar sx={{ color: 'primary.main', bgcolor: 'primary.lighter' }}>
                              <MessageOutlined />
                            </Avatar>
                          </ListItemAvatar>

                          <ListItemText
                            primary={
                              <Typography variant="h6" noWrap>
                                Message incoming ({n.unread})
                              </Typography>
                            }
                            secondary={
                              <Box>
                                <Typography variant="caption" sx={{ display: 'block', opacity: 0.75 }} noWrap>
                                  session: {n.sessionName} • chat: {n.chatId}
                                </Typography>
                                <Typography variant="body2" sx={{ display: 'block' }} noWrap>
                                  {n.preview}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))
                    )}
                  </List>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}
